import React from 'react';
import { ArrowLeftRightIcon, FileTextIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { Band, PullQuote } from './Band';
import { sessionSteps } from '../../data/site';
const aiColumn = {
  icon: SparklesIcon,
  title: 'The AI assistant',
  hasLabel: 'Can',
  lacksLabel: 'Only sees',
  has: ['Reason across shared evidence', 'Analyse documents', 'Answer follow-up questions', 'Revise its conclusions'],
  lacks: ["The candidate's prompts", 'Documents the candidate chooses to share', 'The conversation so far']
};
const sourceColumn = {
  icon: FileTextIcon,
  title: 'The source files',
  hasLabel: 'Contain',
  lacksLabel: 'The candidate decides',
  has: ['The evidence needed to complete the task', 'Supporting context', 'Information that may confirm or contradict the AI'],
  lacks: ['What evidence the AI needs', 'When to share it', 'Whether to investigate further']
};
/** Read left to right: the evidence, the person who controls it, then the model. */
const asymmetry = [sourceColumn, aiColumn];
const phaseWidths = ['4%', '6%', '78%', '12%'];
export function HowItWorks() {
  return <Band id="how" eyebrow="How Lentic works" title="The AI can reason. The candidate decides what it sees." description="Lentic gives candidates AI and source documents. They decide what evidence to share with the AI, then what to verify, challenge and ultimately trust in reaching their decision." aside={<PullQuote data-id="element-191" data-mp="ohcijsio232d">
          The candidate decides what evidence the AI needs, when to bring it in, what to trust, what to challenge and when to investigate further.
        </PullQuote>} data-id="element-190" data-mp="kyorheoklt7">
      <p className="mb-10 font-display text-2xl font-light leading-snug text-ink" data-id="element-192" data-mp="y8gvkmos7duk">
        We don't detect AI use. We assume it.
      </p>

      <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch" data-id="element-193" data-mp="t9coq2pbxs7e">
        {asymmetry.map((col, i) => <React.Fragment key={col.title} data-id="element-194" data-mp="178vjltt4r71r" data-mpi={i}>
            <figure className="rounded-2xl border border-mist bg-white p-8 shadow-[0_30px_70px_-60px_rgba(14,36,54,0.6)]" data-id="element-195" data-mp="137absmvgdiyf">
              <figcaption className="flex items-center gap-3" data-id="element-196" data-mp="1ab9sdo7i9gdb">
                <col.icon className="h-5 w-5 text-teal" aria-hidden="true" data-id="element-197" data-mp="1f9bcmcgo3lqd" />
                <span className="font-display text-2xl font-light text-ink" data-id="element-198" data-mp="1f77a9yyf2us7">{col.title}</span>
              </figcaption>
              <dl className="mt-7 space-y-6" data-id="element-199" data-mp="1oz4mn915vssi">
                <div data-id="element-200" data-mp="1ozwjen6hvwkd">
                  <dt className="font-mono text-xs uppercase tracking-[0.14em] text-teal" data-id="element-201" data-mp="1p0fq17qp2yj3">
                    {col.hasLabel}
                  </dt>
                  <dd className="mt-2 flex flex-wrap gap-2" data-id="element-202" data-mp="1xz9qxab26b2g">
                    {col.has.map(item => <span key={item} className="rounded-lg border border-teal/25 bg-teal/5 px-3 py-1.5 text-sm text-ink" data-id="element-203" data-mp="22z5lkixv8q5c">
                        {item}
                      </span>)}
                  </dd>
                </div>
                <div data-id="element-204" data-mp="8lmpzenebhmo">
                  <dt className="font-mono text-xs uppercase tracking-[0.14em] text-clay" data-id="element-205" data-mp="8mfra03l5exf">
                    {col.lacksLabel}
                  </dt>
                  <dd className="mt-2 flex flex-wrap gap-2" data-id="element-206" data-mp="2qtza5gw29pzj">
                    {col.lacks.map(item => <span key={item} className="rounded-lg border border-mist bg-bone px-3 py-1.5 text-sm text-graphite" data-id="element-207" data-mp="2m3ivjtgi7ybl">
                        {item}
                      </span>)}
                  </dd>
                </div>
              </dl>
            </figure>

            {i === 0 ? <div className="flex flex-col items-center justify-center gap-3 py-2 lg:w-52 lg:py-0" data-id="element-208" data-mp="2d4nlg6aca430">
                <div className="hidden h-full w-px bg-mist lg:block" data-id="element-209" data-mp="39qzcy128e8nr" />
                <div className="flex flex-col items-center gap-2.5 rounded-2xl bg-ink px-5 py-6 text-center text-bone" data-id="element-210" data-mp="35st8x0ss276j">
                  <UserIcon className="h-6 w-6 text-teal-light" aria-hidden="true" data-id="element-211" data-mp="30qkgc5jth74y" />
                  <span className="font-display text-lg" data-id="element-212" data-mp="2vssmh5zm3ay4">The candidate</span>
                  <ArrowLeftRightIcon className="h-4 w-4 text-teal-light" aria-hidden="true" data-id="element-213" data-mp="1pe9u6hzkw99h" />
                  <span className="font-display text-xl font-light text-bone" data-id="element-214" data-mp="1kdvubn84s5mj">
                    Controls the evidence
                  </span>
                  <span className="max-w-[12rem] text-[13px] leading-snug text-bone/65" data-id="element-215" data-mp="388qnznwr02sz">
                    Decides what the AI sees, what to verify, and what to trust.
                  </span>
                </div>
                <div className="hidden h-full w-px bg-mist lg:block" data-id="element-216" data-mp="3499hqs9hz1mr" />
              </div> : null}
          </React.Fragment>)}
      </div>

      <div className="mt-20" data-id="element-217" data-mp="3ryknyp9m9o8a">
        <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-218" data-mp="3rzc6ozi47rh5">
          <h3 className="font-display text-3xl font-light text-ink" data-id="element-219" data-mp="3vzeb3gf8iz04">
            One session, 60–90 minutes
          </h3>
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-graphite" data-id="element-220" data-mp="3i0elo6jweg2w">
            Browser sandbox · no install
          </p>
        </div>

        <div className="mt-8 flex h-3 w-full overflow-hidden rounded-full border border-mist bg-white" aria-hidden="true" data-id="element-221" data-mp="26kybz86oiodt">
          {phaseWidths.map((w, i) => <span key={w} style={{
          width: w,
          paddingLeft: "40px"
        }} className={i === 2 ? 'block bg-teal' : i === 3 ? 'block bg-teal/50' : 'block bg-ink/25'} data-id="element-222" data-mp="2blvq99okiya1" data-mpi={i} />)}
        </div>

        <ol className="mt-6 grid gap-px overflow-hidden rounded-2xl border border-mist bg-mist md:grid-cols-4" data-id="element-223" data-mp="1z42w3jd7ftu7">
          {sessionSteps.map(step => <li key={step.title} className="flex flex-col bg-bone p-6" data-id="element-224" data-mp="282544ivcvmqz">
              <span className="font-mono text-xs uppercase tracking-[0.12em] text-teal" data-id="element-225" data-mp="280u1imdncl1g">
                {step.time}
              </span>
              <h4 className="mt-3 font-display text-xl text-ink" data-id="element-226" data-mp="1gca3vr5ymcnj">{step.title}</h4>
              <p className="mt-2 text-[15px] leading-relaxed text-graphite" data-id="element-227" data-mp="1u2lypxtxipid">
                {step.body}
              </p>
            </li>)}
        </ol>
      </div>
    </Band>;
}